(() => {
  const roots = new WeakSet();
  const requests = new WeakMap();
  const replyRequests = new WeakMap();
  const pendingActions = new WeakMap();
  const feedPages = new WeakMap();
  const EVENTS = {
    feedViewed: 'fw:community:feed-viewed',
    postOpened: 'fw:community:post-opened',
    commentSubmitted: 'fw:community:comment-submitted',
    replySubmitted: 'fw:community:reply-submitted',
    commentReported: 'fw:community:comment-reported',
    productSelected: 'fw:community:product-selected',
    loginRequested: 'fw:community:login-requested',
    videoStarted: 'fw:community:video-started',
    videoCompleted: 'fw:community:video-completed',
  };

  const select = (root, selector) => root.querySelector(selector);
  const selectAll = (root, selector) => [...root.querySelectorAll(selector)];

  function dispatch(root, name, detail = {}) {
    root.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail,
    }));
  }

  function element(tagName, className, text) {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  }

  function endpoint(root, path) {
    const origin = window.location.origin;
    const configuredRoot = String(root.dataset.apiRoot || '/apps/formwynn-community').trim();
    let apiRoot;
    try {
      apiRoot = new URL(configuredRoot, origin);
    } catch (_error) {
      apiRoot = null;
    }
    const apiPath = apiRoot?.pathname.replace(/\/+$/, '') || '';
    if (
      !apiRoot
      || apiRoot.origin !== origin
      || !apiPath.startsWith('/apps/')
      || apiRoot.search
      || apiRoot.hash
      || !path.startsWith('/')
    ) {
      const error = new Error('Community is unavailable because its App Proxy path is invalid.');
      error.code = 'invalid_api_root';
      error.retryable = false;
      throw error;
    }
    return new URL(`${apiPath}${path}`, origin).toString();
  }

  function apiError(payload, status) {
    const detail = payload?.ok === false && payload.error && typeof payload.error === 'object'
      ? payload.error
      : {};
    const message = typeof detail.message === 'string' && detail.message.trim()
      ? detail.message
      : `Request failed (${status}).`;
    const error = new Error(message);
    error.code = typeof detail.code === 'string' && detail.code ? detail.code : 'request_failed';
    error.retryable = detail.retryable === true;
    error.status = status;
    return error;
  }

  async function requestJson(url, options = {}) {
    const { headers: optionHeaders = {}, ...requestOptions } = options;
    const response = await fetch(url, {
      ...requestOptions,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...optionHeaders,
      },
    });
    let payload = null;
    try {
      payload = await response.json();
    } catch (_error) {
      payload = null;
    }
    if (!response.ok || payload?.ok !== true) throw apiError(payload, response.status);
    return payload.data ?? {};
  }

  function safeMediaUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'https:' ? url.toString() : null;
    } catch (_error) {
      return null;
    }
  }

  function safeStorefrontUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : null;
    } catch (_error) {
      return null;
    }
  }

  function safeHandle(value) {
    return String(value || '').replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  function createGalleryMedia(root, post) {
    const mediaWrap = element('div', 'fw-community-gallery__media');
    if (post.mediaType === 'video') {
      const videoUrl = safeMediaUrl(post.videoUrl);
      const posterUrl = safeMediaUrl(post.posterUrl);
      const videoMimeType = String(post.videoMimeType || '').toLocaleLowerCase('en-US');
      if (!videoUrl || !posterUrl || videoMimeType !== 'video/mp4') return null;
      const video = element('video', 'fw-community-gallery__video');
      video.controls = true;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.poster = posterUrl;
      video.setAttribute('aria-label', String(post.accessibleDescription));
      const source = element('source');
      source.src = videoUrl;
      source.type = videoMimeType;
      video.append(source);
      mediaWrap.append(video);
    } else {
      const imageUrl = safeMediaUrl(post.imageUrl);
      if (!imageUrl) return null;
      const image = element('img', 'fw-community-gallery__image');
      image.src = imageUrl;
      image.alt = String(post.accessibleDescription);
      image.loading = post.featured ? 'eager' : 'lazy';
      image.decoding = 'async';
      mediaWrap.append(image);
    }
    if (post.featured && root.dataset.featuredLabel) {
      mediaWrap.append(element('span', 'fw-community-gallery__featured-label', root.dataset.featuredLabel));
    }
    return mediaWrap;
  }

  function createGalleryCard(root, post, index) {
    const card = element('article', `fw-community-gallery__item${post.featured ? ' fw-community-gallery__item--featured' : ''}`);
    card.dataset.communityGalleryPost = '';
    card.dataset.postHandle = String(post.handle);
    if (post.featured) card.dataset.communityFeatured = '';

    const mediaWrap = createGalleryMedia(root, post);
    if (!mediaWrap) return null;

    const content = element('div', 'fw-community-gallery__caption');
    const meta = element('div', 'fw-community-gallery__meta');
    meta.append(
      element('span', '', post.movement || ''),
      element('span', '', String(index + 1).padStart(2, '0'))
    );
    content.append(meta);
    if (String(post.caption || '').trim()) content.append(element('h3', 'fw-site__serif fw-community-gallery__title', post.caption));

    const productUrl = safeStorefrontUrl(post.productUrl);
    if (productUrl && post.productTitle) {
      const product = element('a', 'fw-site__link fw-community-gallery__product', post.productTitle);
      product.href = productUrl;
      product.dataset.communityProductLink = '';
      content.append(product);
    }

    if (root.dataset.showDiscussions === 'true') {
      const discussion = element(
        'a',
        'fw-community-gallery__discussion-link',
        root.dataset.galleryDiscussionLabel || 'JOIN THE DISCUSSION'
      );
      discussion.href = `#CommunityDiscussion-${safeHandle(post.handle)}`;
      discussion.dataset.communityDiscussionLink = '';
      discussion.dataset.postHandle = String(post.handle);
      content.append(discussion);
    }
    card.append(mediaWrap, content);
    return card;
  }

  function createDiscussionTopic(root, post, index) {
    const card = element('article', 'fw-community-post fw-community-discussion');
    card.dataset.communityPost = '';
    card.dataset.postHandle = String(post.handle);
    card.id = `CommunityDiscussion-${safeHandle(post.handle)}`;

    const content = element('div', 'fw-community-post__content fw-community-discussion__content');
    const meta = element('div', 'fw-community-post__meta fw-community-discussion__meta');
    meta.append(
      element('span', '', String(index + 1).padStart(2, '0')),
      element('span', '', post.movement || '')
    );
    const title = element('h3', 'fw-site__serif fw-community-post__title fw-community-discussion__title', String(post.caption || '').trim() || post.title);
    content.append(meta, title);

    const productUrl = safeStorefrontUrl(post.productUrl);
    if (productUrl && post.productTitle) {
      const product = element('a', 'fw-site__link fw-community-post__product', post.productTitle);
      product.href = productUrl;
      product.dataset.communityProductLink = '';
      content.append(product);
    }

    if (root.dataset.showComments !== 'false') {
      const detail = element('details', 'fw-community-thread fw-community-discussion__thread');
      detail.dataset.communityDetail = '';
      detail.append(element('summary', 'fw-community-thread__summary fw-community-discussion__summary', root.dataset.threadLabel || 'VIEW THE CONVERSATION'));
      const body = element('div', 'fw-community-thread__body');
      const mount = element('div', 'fw-community-thread__mount');
      mount.dataset.communityThread = '';
      mount.setAttribute('aria-live', 'polite');
      mount.append(element('p', '', root.dataset.loadingMessage || 'Loading the conversation…'));
      const retry = element('button', 'fw-community-thread__retry', root.dataset.retryMessage || 'TRY AGAIN');
      retry.type = 'button';
      retry.dataset.communityRetry = '';
      retry.hidden = true;
      body.append(mount, retry);
      if (root.dataset.canComment === 'true') {
        const form = element('form', 'fw-community-form');
        form.dataset.communityCommentForm = '';
        const fieldId = `CommunityComment-${String(post.handle).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
        const label = element('label', '', root.dataset.commentLabel || 'Join the conversation');
        label.htmlFor = fieldId;
        const textarea = element('textarea');
        textarea.id = fieldId;
        textarea.name = 'body';
        textarea.maxLength = 800;
        textarea.required = true;
        const submit = element('button', '', root.dataset.commentButtonLabel || 'POST COMMENT');
        submit.type = 'submit';
        const formStatus = element('p');
        formStatus.dataset.communityFormStatus = '';
        formStatus.setAttribute('role', 'status');
        form.append(label, textarea, submit, formStatus);
        body.append(form);
      } else if (root.dataset.loginUrl) {
        const login = element('a', 'fw-site__link fw-community-thread__login', root.dataset.signInLabel || 'SIGN IN TO COMMENT');
        login.href = root.dataset.loginUrl;
        login.dataset.communityLogin = '';
        body.append(login);
      }
      detail.append(body);
      content.append(detail);
    }

    card.append(content);
    return card;
  }

  function eligiblePresentation(post) {
    if (!post || typeof post !== 'object') return false;
    if (!post.id || !post.handle || !post.title || !post.accessibleDescription) return false;
    if (post.mediaType === 'image') return Boolean(safeMediaUrl(post.imageUrl)) && !post.videoUrl;
    if (post.mediaType === 'video') {
      return post.videoMimeType === 'video/mp4' && Boolean(safeMediaUrl(post.videoUrl)) && Boolean(safeMediaUrl(post.posterUrl)) && !post.imageUrl;
    }
    return false;
  }

  function clearCommunityViews(root, message = '') {
    const gallery = select(root, '[data-community-gallery]');
    const discussions = select(root, '[data-community-discussions]');
    const status = select(root, '[data-community-feed-status]');
    const discussionStatus = select(root, '[data-community-discussion-status]');
    gallery?.replaceChildren();
    discussions?.replaceChildren();
    if (gallery) gallery.hidden = true;
    if (discussions) discussions.hidden = true;
    if (status) {
      status.hidden = false;
      status.textContent = message;
    }
    if (discussionStatus) {
      discussionStatus.hidden = false;
      discussionStatus.textContent = message;
    }
  }

  function reconcileFeed(root, posts, append = false) {
    const gallery = select(root, '[data-community-gallery]');
    const discussions = select(root, '[data-community-discussions]');
    const status = select(root, '[data-community-feed-status]');
    const discussionStatus = select(root, '[data-community-discussion-status]');
    if (!gallery) return [];
    const showDiscussions = root.dataset.showDiscussions === 'true' && Boolean(discussions);
    const combined = append ? [...(feedPages.get(root) || []), ...(Array.isArray(posts) ? posts : [])] : (Array.isArray(posts) ? posts : []);
    const unique = new Map();
    for (const post of combined.filter(eligiblePresentation)) unique.set(String(post.id || post.handle), post);
    const ordered = [...unique.values()];
    const galleryCards = ordered.map((post, index) => createGalleryCard(root, post, index)).filter(Boolean);
    const topics = showDiscussions
      ? ordered.map((post, index) => createDiscussionTopic(root, post, index)).filter(Boolean)
      : [];
    if (showDiscussions && galleryCards.length !== topics.length) {
      clearCommunityViews(root, root.dataset.retryMessage || 'Community is unavailable.');
      return [];
    }
    feedPages.set(root, ordered);
    gallery.replaceChildren(...galleryCards);
    discussions?.replaceChildren(...topics);
    gallery.hidden = galleryCards.length === 0;
    if (discussions) discussions.hidden = topics.length === 0;
    if (status) {
      status.textContent = galleryCards.length ? '' : (root.dataset.emptyMessage || 'No stories are published yet.');
      status.hidden = galleryCards.length > 0;
    }
    if (showDiscussions && discussionStatus) {
      discussionStatus.textContent = topics.length ? '' : (root.dataset.emptyMessage || 'No stories are published yet.');
      discussionStatus.hidden = topics.length > 0;
    }
    return galleryCards;
  }

  async function loadFeed(root, cursor = null, append = false) {
    const gallery = select(root, '[data-community-gallery]');
    const discussions = select(root, '[data-community-discussions]');
    const status = select(root, '[data-community-feed-status]');
    const discussionStatus = select(root, '[data-community-discussion-status]');
    const more = select(root, '[data-community-feed-more]');
    if (!gallery) return;
    if (!append) {
      feedPages.delete(root);
      gallery.replaceChildren();
      discussions?.replaceChildren();
      gallery.hidden = true;
      if (discussions) discussions.hidden = true;
    }
    if (more) {
      more.disabled = true;
      more.dataset.normalLabel ||= more.textContent || root.dataset.loadMoreStoriesLabel || 'LOAD MORE STORIES';
      more.textContent = root.dataset.loadingMessage || 'Loading…';
      if (!append) more.hidden = true;
    }
    if (status) {
      status.hidden = false;
      status.textContent = root.dataset.loadingMessage || 'Loading the Community…';
    }
    if (discussionStatus) {
      discussionStatus.hidden = false;
      discussionStatus.textContent = root.dataset.loadingMessage || 'Loading the Community…';
    }
    try {
      const pageSize = Math.min(24, Math.max(3, Number(root.dataset.pageSize) || 12));
      const query = `page_size=${pageSize}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
      const payload = await requestJson(endpoint(root, `/posts?${query}`));
      reconcileFeed(root, payload.posts, append);
      const nextCursor = typeof payload.nextCursor === 'string' && payload.nextCursor ? payload.nextCursor : null;
      if (more) {
        more.dataset.cursor = nextCursor || '';
        more.textContent = more.dataset.normalLabel;
        more.disabled = false;
        more.hidden = !nextCursor;
      }
    } catch (error) {
      if (!append) {
        feedPages.delete(root);
        clearCommunityViews(root, error.message || root.dataset.retryMessage || 'Community is unavailable.');
      }
      if (status && append) {
        status.hidden = false;
        status.textContent = error.message || root.dataset.retryMessage || 'Community is unavailable.';
      }
      if (discussionStatus && append) {
        discussionStatus.hidden = false;
        discussionStatus.textContent = error.message || root.dataset.retryMessage || 'Community is unavailable.';
      }
      if (more && append) {
        more.disabled = false;
        more.hidden = false;
        more.textContent = `${more.dataset.normalLabel || 'LOAD MORE STORIES'} · TRY AGAIN`;
      }
    }
  }

  function formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }

  function createReplyForm(root, commentId) {
    const form = element('form', 'fw-community-form fw-community-form--reply');
    form.dataset.communityReplyForm = '';
    form.dataset.parentCommentId = String(commentId);
    form.hidden = true;

    const fieldId = `CommunityReply-${String(commentId).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    const label = element('label', '', root.dataset.replyLabel || 'Write a reply');
    label.htmlFor = fieldId;
    const textarea = element('textarea');
    textarea.id = fieldId;
    textarea.name = 'body';
    textarea.maxLength = 800;
    textarea.required = true;
    const button = element('button', '', root.dataset.replyButtonLabel || 'POST REPLY');
    button.type = 'submit';
    const status = element('p');
    status.dataset.communityFormStatus = '';
    status.setAttribute('role', 'status');

    form.append(label, textarea, button, status);
    return form;
  }

  function createComment(root, comment, isReply = false) {
    const item = element('li', 'fw-community-comment');
    item.dataset.commentId = String(comment.id || '');

    const header = element('div', 'fw-community-comment__header');
    header.append(
      element('strong', '', comment.displayName || comment.display_name || 'Member'),
      element('time', '', formatDate(comment.createdAt || comment.created_at))
    );
    const body = element('p', 'fw-community-comment__body', comment.body || '');
    item.append(header, body);

    if (root.dataset.canComment === 'true') {
      const actions = element('div', 'fw-community-comment__actions');
      if (!isReply) {
        const reply = element('button', '', root.dataset.replyLabel || 'Reply');
        reply.type = 'button';
        reply.dataset.communityReplyToggle = '';
        reply.setAttribute('aria-expanded', 'false');
        actions.append(reply);
      }
      const report = element('button', '', root.dataset.reportLabel || 'REPORT');
      report.type = 'button';
      report.dataset.communityReport = '';
      report.dataset.commentId = String(comment.id || '');
      actions.append(report);
      item.append(actions);

      if (!isReply) item.append(createReplyForm(root, comment.id || ''));
    }

    const replies = Array.isArray(comment.replies) ? comment.replies : [];
    const nextRepliesCursor = typeof comment.nextRepliesCursor === 'string'
      && comment.nextRepliesCursor ? comment.nextRepliesCursor : null;
    if (!isReply && (replies.length || nextRepliesCursor)) {
      const replyList = element('ol', 'fw-community-thread__replies');
      const safeCommentId = String(comment.id || '').replace(/[^a-zA-Z0-9_-]/g, '-');
      replyList.id = `CommunityReplies-${safeCommentId}`;
      replyList.dataset.communityReplyList = '';
      for (const reply of replies) replyList.append(createComment(root, reply, true));
      item.append(replyList);
      if (nextRepliesCursor) {
        const more = element(
          'button',
          'fw-community-thread__more fw-community-thread__more--replies',
          root.dataset.loadMoreRepliesLabel || 'LOAD MORE REPLIES'
        );
        more.type = 'button';
        more.dataset.communityRepliesMore = '';
        more.dataset.parentCommentId = String(comment.id || '');
        more.dataset.cursor = nextRepliesCursor;
        more.dataset.normalLabel = more.textContent;
        more.setAttribute('aria-controls', replyList.id);
        more.setAttribute('aria-live', 'polite');
        item.append(more);
      }
    }

    return item;
  }

  function renderThread(root, post, payload, append = false) {
    const mount = select(post, '[data-community-thread]');
    if (!mount) return;

    const comments = Array.isArray(payload.comments) ? payload.comments : [];
    let list = append ? select(mount, '.fw-community-thread__list') : null;
    if (!append) mount.replaceChildren();
    if (!comments.length && !list) {
      mount.append(element('p', '', root.dataset.emptyThreadMessage || 'Start the conversation.'));
      return;
    }

    if (!list) list = element('ol', 'fw-community-thread__list');
    for (const comment of comments) list.append(createComment(root, comment));
    mount.replaceChildren(list);

    const nextCursor = typeof payload.nextCursor === 'string' && payload.nextCursor
      ? payload.nextCursor
      : null;
    post.dataset.communityThreadCursor = nextCursor || '';
    if (nextCursor) {
      const more = element(
        'button',
        'fw-community-thread__more',
        root.dataset.loadMoreLabel || 'LOAD MORE COMMENTS'
      );
      more.type = 'button';
      more.dataset.communityThreadMore = '';
      more.dataset.cursor = nextCursor;
      mount.append(more);
    }
  }

  function showThreadError(root, post, message) {
    const mount = select(post, '[data-community-thread]');
    const retry = select(post, '[data-community-retry]');
    if (mount) mount.replaceChildren(element('p', '', message || root.dataset.retryMessage));
    if (retry) retry.hidden = false;
  }

  function showProfile(root, intendedForm, message) {
    const panel = select(root, '[data-community-profile]');
    const status = panel && select(panel, '[data-community-profile-status]');
    const input = panel && select(panel, 'input[name="displayName"]');
    if (!panel || !input) {
      const formStatus = select(intendedForm, '[data-community-form-status]');
      if (formStatus) formStatus.textContent = message;
      return;
    }
    pendingActions.set(root, intendedForm);
    panel.hidden = false;
    if (status) status.textContent = message || '';
    input.focus();
  }

  async function loadThread(root, post, cursor = null, append = false) {
    const handle = post.dataset.postHandle;
    if (!handle) return;

    const prior = requests.get(post);
    if (prior) prior.abort();
    const controller = new AbortController();
    requests.set(post, controller);

    const retry = select(post, '[data-community-retry]');
    const mount = select(post, '[data-community-thread]');
    const more = select(post, '[data-community-thread-more]');
    if (retry) retry.hidden = true;
    if (more) more.disabled = true;
    if (mount && !append) {
      mount.replaceChildren(element('p', '', root.dataset.loadingMessage || 'Loading the conversation…'));
    }

    try {
      const query = `page_size=12${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
      const payload = await requestJson(endpoint(
        root,
        `/posts/${encodeURIComponent(handle)}?${query}`
      ), {
        signal: controller.signal,
      });
      if (requests.get(post) !== controller) return;
      renderThread(root, post, payload, append);
      post.dataset.communityThreadLoaded = 'true';
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (append && more) {
        more.disabled = false;
        more.textContent = error.message;
      } else {
        showThreadError(root, post, error.message);
      }
    } finally {
      if (requests.get(post) === controller) requests.delete(post);
    }
  }

  async function loadReplies(root, post, button) {
    const parentCommentId = button?.dataset.parentCommentId;
    const cursor = button?.dataset.cursor;
    const item = button?.closest('.fw-community-comment');
    const replyList = item && select(item, '[data-community-reply-list]');
    if (!post || !parentCommentId || !cursor || !replyList || button.disabled) return;

    const prior = replyRequests.get(button);
    if (prior) prior.abort();
    const controller = new AbortController();
    replyRequests.set(button, controller);
    button.disabled = true;

    try {
      const query = `page_size=12&cursor=${encodeURIComponent(cursor)}`;
      const payload = await requestJson(endpoint(
        root,
        `/comments/${encodeURIComponent(parentCommentId)}/replies?${query}`
      ), { signal: controller.signal });
      if (replyRequests.get(button) !== controller) return;

      const knownIds = new Set(
        selectAll(replyList, '[data-comment-id]').map(node => node.dataset.commentId)
      );
      const replies = Array.isArray(payload.replies) ? payload.replies : [];
      for (const reply of replies) {
        const replyId = String(reply?.id || '');
        if (!replyId || knownIds.has(replyId)) continue;
        knownIds.add(replyId);
        replyList.append(createComment(root, reply, true));
      }

      const nextCursor = typeof payload.nextCursor === 'string' && payload.nextCursor
        ? payload.nextCursor
        : null;
      if (nextCursor) {
        button.dataset.cursor = nextCursor;
        button.textContent = button.dataset.normalLabel || 'LOAD MORE REPLIES';
        button.disabled = false;
      } else {
        button.remove();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      button.textContent = error.message;
      button.disabled = false;
    } finally {
      if (replyRequests.get(button) === controller) replyRequests.delete(button);
    }
  }

  async function submitForm(root, form) {
    const post = form.closest('[data-community-post]');
    if (!post) return;
    const data = new FormData(form);
    const body = String(data.get('body') || '');
    const status = select(form, '[data-community-form-status]');
    const button = select(form, 'button[type="submit"]');
    const handle = post.dataset.postHandle;
    const parentCommentId = form.dataset.parentCommentId;
    const isReply = form.matches('[data-community-reply-form]');
    const path = isReply
      ? `/comments/${encodeURIComponent(parentCommentId)}/replies`
      : `/posts/${encodeURIComponent(handle)}/comments`;

    if (status) status.textContent = '';
    if (button) button.disabled = true;
    try {
      const result = await requestJson(endpoint(root, path), {
        method: 'POST',
        body: JSON.stringify({ body }),
      });
      form.reset();
      if (status) {
        status.textContent = result.status === 'held'
          ? 'Held for review.'
          : (isReply ? 'Reply posted.' : 'Comment posted.');
      }
      dispatch(root, isReply ? EVENTS.replySubmitted : EVENTS.commentSubmitted, { postHandle: handle });
      await loadThread(root, post);
    } catch (error) {
      if (error.code === 'profile_required') {
        showProfile(root, form, error.message);
      } else if (status) {
        status.textContent = error.message;
      }
    } finally {
      if (button) button.disabled = false;
    }
  }

  async function submitProfile(root, form) {
    const data = new FormData(form);
    const displayName = String(data.get('displayName') || '').trim();
    const status = select(form, '[data-community-profile-status]');
    const button = select(form, 'button[type="submit"]');
    const panel = form.closest('[data-community-profile]');
    if (!displayName) return;

    if (status) status.textContent = '';
    if (button) button.disabled = true;
    try {
      await requestJson(endpoint(root, '/profile'), {
        method: 'POST',
        body: JSON.stringify({ displayName }),
      });
      const intendedForm = pendingActions.get(root);
      pendingActions.delete(root);
      form.reset();
      if (panel) panel.hidden = true;
      if (intendedForm) await submitForm(root, intendedForm);
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      if (button) button.disabled = false;
    }
  }

  async function reportComment(root, button) {
    const post = button.closest('[data-community-post]');
    const commentId = button.dataset.commentId;
    if (!post || !commentId || button.disabled) return;
    button.disabled = true;
    try {
      await requestJson(endpoint(root, `/comments/${encodeURIComponent(commentId)}/report`), {
        method: 'POST',
        body: JSON.stringify({ reason: 'member_report' }),
      });
      button.textContent = 'REPORTED';
      dispatch(root, EVENTS.commentReported, { postHandle: post.dataset.postHandle });
    } catch (error) {
      button.textContent = error.message;
      button.disabled = false;
    }
  }

  function openDiscussion(root, handle) {
    const topic = selectAll(root, '[data-community-post]')
      .find(node => node.dataset.postHandle === String(handle || ''));
    const detail = topic && select(topic, '[data-community-detail]');
    const summary = detail && select(detail, 'summary');
    if (!topic || !detail || !summary) return false;
    detail.open = true;
    if (topic.dataset.communityThreadLoaded !== 'true') loadThread(root, topic);
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    topic.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    summary.focus?.();
    dispatch(root, EVENTS.postOpened, { postHandle: topic.dataset.postHandle || '' });
    return true;
  }

  function recordFeedView(root) {
    const record = () => dispatch(root, EVENTS.feedViewed, {
      visiblePosts: selectAll(root, '[data-community-post]').length,
    });
    if (!('IntersectionObserver' in window)) {
      record();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some(({ isIntersecting }) => isIntersecting)) return;
      observer.disconnect();
      record();
    }, { threshold: 0.15 });
    observer.observe(root);
  }

  function initialise(root) {
    if (roots.has(root)) return;
    roots.add(root);

    if (root.dataset.communityPreview === 'true') {
      root.addEventListener('submit', event => event.preventDefault());
      return;
    }

    root.addEventListener('toggle', (event) => {
      const detail = event.target.closest('[data-community-detail]');
      if (!detail || !detail.open) return;
      const post = detail.closest('[data-community-post]');
      dispatch(root, EVENTS.postOpened, { postHandle: post?.dataset.postHandle || '' });
      if (post && post.dataset.communityThreadLoaded !== 'true') loadThread(root, post);
    }, true);

    root.addEventListener('submit', (event) => {
      const profileForm = event.target.closest('[data-community-profile-form]');
      if (profileForm) {
        event.preventDefault();
        submitProfile(root, profileForm);
        return;
      }
      const form = event.target.closest('[data-community-comment-form], [data-community-reply-form]');
      if (!form) return;
      event.preventDefault();
      submitForm(root, form);
    });

    root.addEventListener('click', (event) => {
      const discussionLink = event.target.closest('[data-community-discussion-link]');
      if (discussionLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button !== 1) {
        event.preventDefault();
        openDiscussion(root, discussionLink.dataset.postHandle);
        return;
      }

      const retry = event.target.closest('[data-community-retry]');
      if (retry) {
        loadThread(root, retry.closest('[data-community-post]'));
        return;
      }

      const feedMore = event.target.closest('[data-community-feed-more]');
      if (feedMore?.dataset.cursor && !feedMore.disabled) {
        loadFeed(root, feedMore.dataset.cursor, true);
        return;
      }

      const more = event.target.closest('[data-community-thread-more]');
      if (more) {
        const post = more.closest('[data-community-post]');
        if (post && more.dataset.cursor) loadThread(root, post, more.dataset.cursor, true);
        return;
      }

      const moreReplies = event.target.closest('[data-community-replies-more]');
      if (moreReplies) {
        const post = moreReplies.closest('[data-community-post]');
        if (post) loadReplies(root, post, moreReplies);
        return;
      }

      const replyToggle = event.target.closest('[data-community-reply-toggle]');
      if (replyToggle) {
        const item = replyToggle.closest('.fw-community-comment');
        const form = select(item, '[data-community-reply-form]');
        if (!form) return;
        const expanded = form.hidden;
        form.hidden = !expanded;
        replyToggle.setAttribute('aria-expanded', String(expanded));
        if (expanded) select(form, 'textarea')?.focus();
        return;
      }

      const report = event.target.closest('[data-community-report]');
      if (report) {
        reportComment(root, report);
        return;
      }

      const product = event.target.closest('[data-community-product-link]');
      if (product) {
        const post = product.closest('[data-community-post], [data-community-gallery-post]');
        dispatch(root, EVENTS.productSelected, { postHandle: post?.dataset.postHandle || '' });
        return;
      }

      const login = event.target.closest('[data-community-login]');
      if (login) dispatch(root, EVENTS.loginRequested);
    });

    root.addEventListener('play', (event) => {
      if (!event.target.matches('.fw-community-post__video, .fw-community-gallery__video')) return;
      const post = event.target.closest('[data-community-post], [data-community-gallery-post]');
      dispatch(root, EVENTS.videoStarted, { postHandle: post?.dataset.postHandle || '' });
    }, true);

    root.addEventListener('ended', (event) => {
      if (!event.target.matches('.fw-community-post__video, .fw-community-gallery__video')) return;
      const post = event.target.closest('[data-community-post], [data-community-gallery-post]');
      dispatch(root, EVENTS.videoCompleted, { postHandle: post?.dataset.postHandle || '' });
    }, true);

    if (root.dataset.mode !== 'editorial') {
      loadFeed(root).then(() => recordFeedView(root));
    } else {
      recordFeedView(root);
    }
  }

  function initialiseAll(scope = document) {
    if (scope.matches?.('[data-community-root]')) initialise(scope);
    selectAll(scope, '[data-community-root]').forEach(initialise);
  }

  if (window.__FORMWYNN_COMMUNITY_TEST_HOOKS__) {
    Object.assign(window.__FORMWYNN_COMMUNITY_TEST_HOOKS__, {
      endpoint,
      loadFeed,
      loadReplies,
      loadThread,
      openDiscussion,
      reconcileFeed,
      requestJson,
      submitForm,
      submitProfile,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }
  document.addEventListener('shopify:section:load', event => initialiseAll(event.target));
})();
